import { Link, useRouterState } from '@tanstack/react-router';
import { CircleHelp, LogOut, MoreHorizontal, UserRound, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { logout } from '../auth.tsx';
import { itemsOf, locate, type NavArea } from '../navigation.ts';

/**
 * Menu no celular "barra embaixo" (ADR 0128): o Início e as primeiras áreas sempre à mão, e
 * "Mais", que abre o menu completo. Só no celular; no computador vale a moldura do layout.
 */
export function BottomBar({ areas, onMore }: { areas: NavArea[]; onMore: () => void }) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = locate(areas, pathname).area;
  const shown = [
    ...areas.filter((a) => a.id === 'home'),
    ...areas.filter((a) => a.id !== 'home' && !a.bottom),
  ].slice(0, 4);
  const cell = 'flex min-h-14 flex-1 flex-col items-center justify-center gap-1 text-[11px]';
  return (
    <nav
      aria-label={t('nav.label')}
      className="fixed inset-x-0 bottom-0 z-30 flex border-t border-rule bg-sheet pb-[env(safe-area-inset-bottom,0px)] md:hidden"
    >
      {shown.map((area) => {
        const Icon = area.icon;
        const active = area.id === current?.id;
        return (
          <Link
            key={area.id}
            to={itemsOf(area)[0]!.to}
            aria-current={active ? 'true' : undefined}
            className={`${cell} ${active ? 'font-medium text-brand-text' : 'text-muted'}`}
          >
            <Icon size={20} strokeWidth={1.75} aria-hidden />
            <span className="max-w-full truncate px-1">{t(area.railKey ?? area.key)}</span>
          </Link>
        );
      })}
      <button type="button" onClick={onMore} className={`${cell} text-muted`}>
        <MoreHorizontal size={20} strokeWidth={1.75} aria-hidden />
        {t('nav.more')}
      </button>
    </nav>
  );
}

/**
 * Menu no celular "grade de áreas" (ADR 0128): todas as áreas em blocos, numa folha que sobe de
 * baixo, com a pessoa, a Ajuda e Sair no pé. Vai dentro do MobileDrawer (foco preso e Esc).
 */
export function AreaGrid({
  areas,
  onNavigate,
  onClose,
}: {
  areas: NavArea[];
  onNavigate: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = locate(areas, pathname).area;
  return (
    <div className="flex max-h-[85vh] flex-col gap-4 overflow-y-auto rounded-t-lg bg-sheet px-4 pt-3 pb-6">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-ink">{t('nav.label')}</p>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('nav.close')}
          className="flex size-11 items-center justify-center rounded-md text-ink"
        >
          <X size={22} aria-hidden />
        </button>
      </div>
      <ul className="grid grid-cols-3 gap-2">
        {areas.map((area) => {
          const Icon = area.icon;
          const active = area.id === current?.id;
          return (
            <li key={area.id}>
              <Link
                to={itemsOf(area)[0]!.to}
                onClick={onNavigate}
                aria-current={active ? 'true' : undefined}
                className={`flex min-h-24 flex-col items-center justify-center gap-2 rounded-lg border p-2 text-center text-xs ${
                  active ? 'ui-nav-active border-transparent font-medium' : 'border-rule text-ink'
                }`}
              >
                <Icon size={24} strokeWidth={1.75} aria-hidden />
                {t(area.railKey ?? area.key)}
              </Link>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center gap-1 border-t border-rule pt-3">
        <Link
          to="/account"
          onClick={onNavigate}
          className="flex min-h-11 flex-1 items-center gap-2 rounded-md px-2 text-sm text-ink"
        >
          <UserRound size={18} strokeWidth={1.75} aria-hidden />
          {t('nav.account')}
        </Link>
        <Link
          to="/help"
          onClick={onNavigate}
          aria-label={t('nav.help')}
          aria-current={pathname === '/help' ? 'page' : undefined}
          className="flex size-11 items-center justify-center rounded-md text-muted"
        >
          <CircleHelp size={20} strokeWidth={1.75} aria-hidden />
        </Link>
        <button
          type="button"
          onClick={() => void logout()}
          className="flex min-h-11 items-center gap-1.5 rounded-md px-2 text-sm text-brand-text"
        >
          <LogOut size={16} strokeWidth={1.75} aria-hidden />
          {t('auth.logout')}
        </button>
      </div>
    </div>
  );
}
